import React, { Suspense, useState, useEffect, useRef, useMemo, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import OfferAssetContainer from 'features/offer/edit/assets/asset/components/OfferAssetContainer';
import OfferAssetListContainer from 'features/offer/edit/assets/OfferAssetListContainer';
import OfferStepper from './OfferStepper';
import { Grid } from '@mui/material';
import { getInt } from 'utils/functions';
import OfferReportsContainer from './reports/OfferReportsContainer';
import OfferFoldersContainer from './folders/OfferFoldersContainer';
import NavigationStepValidationComponent from './navigationStepValidation/NavigationStepValidationComponent';
import { showRightSideBar } from '../../layout/RightSideBar';
import Warnings from 'components/offer/edit/Warnings';
import {
  loadOfferConfiguration,
  resetCurrentOffer,
  submitPreApproval,
  cloneOffer,
  loadOfferWarnings,
  loadNavigationStepWarnings,
  loadOfferReports,
  selectFolder,
  copyOffer
} from 'actions/actionCreators/offer/offerActions';
import { loadDocumentRights } from 'actions/actionCreators/documentRightsActions';
import { showInfo } from 'actions/actionCreators/snackbarActions';
import { setContent, resetContent } from '../../layout/RightSideBar';
import OfferWizardSidebarSummaryContainer from 'features/offer/edit/sidebar/components/summary/OfferWizardSidebarSummaryContainer';
import { RightsContext } from '../../../providers/RightsContext';
import OfferHeader from './OfferHeader';
import Portal from '../../../utils/portal/Portal';
import { areas } from '../../../constants/areas';
import { withAreaGetter } from '../../../providers/AreasConsumers';
import CloneOfferComponent from './cloneOffer/CloneOfferComponent';
import SwitcherComponent from './SwitcherComponent';
import { usePrevious } from 'customHooks/usePrevious';
import ContractContainer from 'features/offer/edit/contract/components/ContractContainer';
import OfferFinancialsContainer from 'features/offer/edit/financials/OfferFinancialsContainer';
import OfferClientContainer from 'features/offer/edit/client/OfferClientContainer';
import { getIsContractModification, getIsFacilityFrame, getIsFinancingOffer } from 'selectors/dictionariesSelectors';
import { getDocumentRightsByStateKey, offerSteps } from 'constants/common';
import InsuranceDocumentDetailsContainer from 'features/offer/edit/insurances/components/InsuranceDocumentDetailsContainer';
import DocumentSubscriptionArea from 'features/common/components/DocumentSubscriptionArea';
import { useTranslation } from 'react-i18next';
import { dismissDocumentWorkflowError } from 'features/document/edit/documents/actionCreators';
import OfferReinitializationComponent from 'features/offer/edit/contract/components/OfferReinitializationComponent';
import { ApplicationFeaturesContext } from 'providers/ApplicationFeaturesContext';
import rightSidebarDrawers from 'constants/rightSidebarDrawers';
import { useTitle } from 'utils/hooks/useTitle';
import { Typography } from '@totalsoft/rocket-ui';
import WrappedFakeText from 'components/common/WrappedFakeText';
import DocumentCommentsContainer from 'features/offer/edit/sidebar/components/comments/DocumentCommentsContainer';

const defaultRights = Object.freeze({ readOnlyFull: true, readOnlyByDocState: true });

const OfferWizard = props => {
  const { t } = useTranslation();
  const { offerWithChecklist, enablePartnerRelations } = useContext(ApplicationFeaturesContext);
  const { documentId, siteId, history, actions } = props;
  const offer = useMemo(() => ({ documentId: getInt(documentId), siteId: getInt(siteId) }), [documentId, siteId]);
  const { offerHeader, config, offerRights, locationPathname, newOffer, getElemByAreaKey } = props;
  const { warnings, warningsIsValid, warningsIsLoaded, rightSidebarDrawer } = props;
  const mounted = useRef(false);
  let firstUpdate = false;

  const [rights, setRights] = useState(defaultRights);
  const { steps, activeStep, onChangeActiveStep } = props;

  const { offerTypeId, documentTypeId, stateId } = offerHeader;

  const isContractModification = getIsContractModification(offerTypeId);
  const isFacilityFrame = getIsFacilityFrame(documentTypeId);
  const isFinancingOffer = getIsFinancingOffer(documentTypeId);
  const [openDuplicateConfirm, setOpenDuplicateForm] = useState(false);
  const [openReinitializationForm, setOpenReinitializationForm] = useState(false);
  const [reinitializationProcessing, setReinitializationProcessing] = useState(false);

  const [openOfferActions, setOpenOfferActions] = useState(false);
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [warningsActiveTab, setWarningsActiveTab] = useState(-1);
  const [showFoldersModal, setShowFoldersModal] = useState(false);
  const [navigationStepValidationMessage, setNavigationStepValidationMessage] = useState(null);
  const [showMessageNavigationStepModal, setShowMessageNavigationStepModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [dirtyComment, setDirtyComment] = useState(false);

  const handleOpenOfferActions = event => {
    setOpenOfferActions(true);
    setAnchorEl(event.currentTarget);
  };
  const handleCloseOfferActions = () => setOpenOfferActions(false);

  const prevDocumentId = usePrevious(documentId);
  const prevSiteId = usePrevious(siteId);

  const setSidebarContent = useCallback(() => {
    switch (rightSidebarDrawer) {
      case rightSidebarDrawers.summary:
        if (dirtyComment) {
          if (window.confirm(t('LeavingWithoutSavingComment'))) {
            setDirtyComment(false);
            setContent(
              <RightsContext.Provider value={rights}>
                <OfferWizardSidebarSummaryContainer documentId={offer.documentId} siteId={offer.siteId} rights={rights} />
              </RightsContext.Provider>
            );
          } else break;
        }
        setContent(
          <RightsContext.Provider value={rights}>
            <OfferWizardSidebarSummaryContainer documentId={offer.documentId} siteId={offer.siteId} rights={rights} />
          </RightsContext.Provider>
        );

        break;
      case rightSidebarDrawers.comments:
        setContent(
          <RightsContext.Provider value={rights}>
            <DocumentCommentsContainer
              documentId={offer.documentId}
              siteId={offer.siteId}
              history={history}
              fullScreen={false}
              dirtyComment={dirtyComment}
              setDirtyComment={setDirtyComment}
            />
          </RightsContext.Provider>
        );
        break;
      default:
        break;
    }
  }, [rightSidebarDrawer, dirtyComment, offer.documentId, offer.siteId, rights, history, t]);

  const setTitle = () => {
    if (isNewOffer) return t('OfferEdit.NewOfferPageTitle');
    else if (isNewContractModification) return t('RestructuringEdit.NewRestructuringPageTitle');
    else if (isNewFacilityFrame) return t('FacilityFrameEdit.NewFacilityFramePageTitle');
    else
      return t('OfferEdit.PageTitle', {
        offerNumber: offerHeader.offerNumber,
        clientName: offerHeader.client.clientName ? `, ${offerHeader.client.clientName}` : ''
      });
  };

  useEffect(() => {
    mounted.current = true;

    if (!isNewOffer && !isNewContractModification && !isNewFacilityFrame) {
      actions.loadOfferWarnings(offer.documentId, offer.siteId);

      if (!mounted.current) return;
      changeStep(0);
      setSidebarContent();
    }

    showRightSideBar(true);
    actions.loadDocumentRights(offer.documentId, offer.siteId, stateId, documentTypeId);
    actions.loadOfferConfiguration(documentTypeId);
    firstUpdate = true; // eslint-disable-line react-hooks/exhaustive-deps

    return () => {
      mounted.current = false;
      actions.resetCurrentOffer(documentId, siteId);
      resetContent();
      showRightSideBar(false);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!firstUpdate) {
      if (!mounted.current) return;

      actions.resetCurrentOffer(prevDocumentId, prevSiteId);

      if (!isNewOffer && !isNewContractModification && !isNewFacilityFrame) {
        changeStep(0);
        setSidebarContent();
        showRightSideBar(true);
      }
    }
  }, [documentId, siteId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!rights) return;
    if (!isNewOffer && !isNewContractModification && !isNewFacilityFrame) {
      setSidebarContent();
    }
  }, [rights, setSidebarContent]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!firstUpdate) {
      if (!mounted.current) return;

      const step = steps.find(
        a =>
          locationPathname.includes(a.route.replace('{id.documentId}', documentId).replace('{id.siteId}', siteId)) ||
          (a.alternativeRoute !== undefined && locationPathname.includes('partner'))
      );

      if (step) {
        onChangeActiveStep(step.id);
      } else changeStep(activeStep);
    }
  }, [locationPathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!firstUpdate) {
      if (!mounted.current) return;

      if (!warningsIsValid) actions.loadOfferWarnings(offer.documentId, offer.siteId);
    }
  }, [warningsIsValid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (warningsIsLoaded) {
      if (warnings.filter(x => x.messageTypeId === 1).length > 0) setWarningsActiveTab(1);
      if (warnings.filter(x => x.messageTypeId === 2).length > 0) setWarningsActiveTab(2);
    }
  }, [warningsIsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (firstUpdate || !mounted.current || offerRights.isLoaded) return;

    actions.loadDocumentRights(offer.documentId, offer.siteId, stateId, documentTypeId);
  }, [actions, documentTypeId, firstUpdate, offer.documentId, offer.siteId, offerRights.isLoaded, stateId]);

  useEffect(() => {
    if (offerRights && offerRights.isLoaded) setRights(offerRights);
  }, [offerRights]);

  useEffect(() => {
    if (offerHeader.folderList && offerHeader.folderList.length > 0) onChangeShowFoldersModal(true);
  }, [offerHeader.folderList]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (offerHeader.validationStepMessage) setShowMessageNavigationStepModal(true);
  }, [offerHeader.validationStepMessage]);

  const onChangeShowReportsModal = value => {
    setShowReportsModal(value);
  };

  const onChangeShowFoldersModal = value => {
    setShowFoldersModal(value);
  };

  const onChangeShowValidationMessageNavigationStep = value => {
    setShowMessageNavigationStepModal(value);
  };

  const onChangeOpenDuplicateConfirmBool = value => {
    setOpenDuplicateForm(value);
  };

  const onChangeOpenReinitializationConfirmBool = value => {
    setOpenReinitializationForm(value);
  };

  const onStepClick = async (step, isRedirect = false, isAsset = false) => {
    const activeStepItem = steps.find(x => x.id === getInt(activeStep));
    if (activeStepItem && step.validateSteps && step.validateSteps.includes(activeStepItem.stepCode)) {
      const warning = await actions.loadNavigationStepWarnings(offer.documentId, offer.siteId, step.id);
      if (warning.isValidationSuccessful === false && locationPathname.split('/')[locationPathname.split('/').length - 1] === 'products') {
        const route = step.route
          .replace('{id.documentId}', offer.documentId)
          .replace('{id.siteId}', offer.siteId)
          .replace('products', 'assets');
        history.push(route);
        return;
      }
      if (warning.isValidationSuccessful === false) {
        setNavigationStepValidationMessage(warning.message);
        setShowMessageNavigationStepModal(true);
        return;
      }
    }

    if (isAsset) history.push(step);
    else {
      if (!offer.documentId) return;

      const route = step.route.replace('{id.documentId}', offer.documentId).replace('{id.siteId}', offer.siteId);

      if (locationPathname === route) return;

      if (isRedirect) history.replace(route);
      else history.push(route);
    }
  };

  const changeStep = stepId => {
    const activeStepIndex = steps.findIndex(x => x.id === getInt(activeStep));
    const clientStepIndex = steps.findIndex(x => x.id === offerSteps.clientStep.id);

    if (clientStepIndex > 0 && offerHeader.client.isPJ && activeStepIndex > clientStepIndex) stepId = offerSteps.clientStep.id;

    onChangeActiveStep(stepId);
    const step = steps.find(a => a.id === stepId);

    if (step) {
      const defaultOfferRoute = `/offers/${documentId}/${siteId}`;

      const isDefaultOfferRoute =
        defaultOfferRoute.toLowerCase() === locationPathname.toLowerCase() ||
        defaultOfferRoute.toLowerCase() + '/' === locationPathname.toLowerCase();

      if (isDefaultOfferRoute) onStepClick(step, true);
      else if (
        locationPathname.split('/')[locationPathname.split('/').length - 2] === 'assets' &&
        !isNaN(parseInt(locationPathname.split('/').pop())) &&
        typeof parseInt(locationPathname.split('/').pop()) == 'number'
      ) {
        onChangeActiveStep(0);
        onStepClick(locationPathname, false, true);
      } else {
        const urlStep = steps.find(a =>
          locationPathname
            .toLowerCase()
            .includes(a.route.replace('{id.documentId}', documentId).replace('{id.siteId}', siteId).toLowerCase())
        );
        if (urlStep) {
          if (urlStep.disabled) {
            onStepClick(step);
          } else {
            onChangeActiveStep(urlStep.id);
            onStepClick(urlStep);
          }
        } else {
          onChangeActiveStep(-1);
        }
      }
    }
  };

  const handleDismissDocumentWorkflowError = useCallback(
    documentWorkflowErrorId => () => {
      if (!warningsIsLoaded) return;

      async function onDismissAsync() {
        await actions.dismissDocumentWorkflowError(documentWorkflowErrorId);
      }

      onDismissAsync();
    },
    [actions, warningsIsLoaded]
  );

  const isNewOffer = newOffer === 'new';
  const isNewContractModification = newOffer === 'restructuring';
  const isNewFacilityFrame = newOffer === 'facilityFrame';

  const offerContainer = route =>
    isContractModification ? (
      <ContractContainer
        documentId={getInt(route.match.params.documentId)}
        siteId={getInt(route.match.params.siteId)}
        isNewContractModification={isNewContractModification}
        isContractModification={isContractModification}
        onChangeOpenReinitializationConfirmBool={onChangeOpenReinitializationConfirmBool}
        reinitializationProcessing={reinitializationProcessing}
      />
    ) : (
      <OfferAssetContainer
        documentId={getInt(route.match.params.documentId)}
        siteId={getInt(route.match.params.siteId)}
        documentDetailId={getInt(route.match.params.documentDetailId)}
        steps={steps}
        activeStep={activeStep}
        config={config}
        history={history}
        isFacilityFrame={isFacilityFrame}
      />
    );

  const createContainer = () =>
    isContractModification ? (
      <ContractContainer isNewContractModification={isNewContractModification} isContractModification={isContractModification} />
    ) : (
      <OfferAssetContainer steps={steps} activeStep={activeStep} history={history} isFacilityFrame={isFacilityFrame} />
    );

  const offerAssetListContainer = route =>
    isContractModification ? (
      <></>
    ) : (
      <OfferAssetListContainer
        documentId={getInt(route.match.params.documentId)}
        siteId={getInt(route.match.params.siteId)}
        processing={offerHeader.processing}
        documentTypeId={offerHeader.documentTypeId}
        isFacilityFrame={isFacilityFrame}
        isContractModification={isContractModification}
      />
    );

  const insuranceDocumentDetailsContainer = route => (
    <InsuranceDocumentDetailsContainer
      offerId={getInt(route.match.params.documentId)}
      offerSiteId={getInt(route.match.params.siteId)}
      expandedDocumentDetail={{
        documentDetailId: getInt(route.match.params.documentDetailId),
        siteId: getInt(route.match.params.documentDetailSiteId),
        assetInformationId: getInt(route.match.params.assetInformationId)
      }}
    />
  );

  const offerFinancialContainer = () => (
    <OfferFinancialsContainer documentId={offer.documentId} siteId={offer.siteId} isFacilityFrame={isFacilityFrame} />
  );

  const offerClientContainer = route => (
    <OfferClientContainer
      documentId={offer.documentId}
      siteId={offer.siteId}
      onChangeActiveStep={onChangeActiveStep}
      partnerId={getInt(route.match.params.partnerId)}
      isFacilityFrame={isFacilityFrame}
    />
  );

  let headerPortalContent;
  if (!isNewOffer && !isNewContractModification && !isNewFacilityFrame) {
    headerPortalContent = (
      <OfferHeader
        data={offerHeader}
        history={history}
        anchorEl={anchorEl}
        isImage={rights.isImage}
        onChangeActiveStep={onChangeActiveStep}
        documentId={offer.documentId}
        siteId={offer.siteId}
        scheduleRead={rights.scheduleRead}
        viewFolder={rights.viewFolder}
        viewWorkflow={rights.viewWorkflow}
        offerWithChecklist={offerWithChecklist}
        clientVerificationsRead={rights.clientVerificationsRead}
        offerClausesRead={rights.offerClausesRead}
        clientRelationsRead={rights.clientRelationsRead}
        documentChecklistRead={rights.documentChecklistRead}
        offerCompetenceorderRead={rights.offerCompetenceorderRead}
        documentReportsRead={rights.documentReportsRead}
        documentPropertiesRead={rights.documentPropertiesRead}
        otherStateTransitionHistoryRead={rights.otherStateTransitionHistoryRead}
        enablePartnerRelations={enablePartnerRelations}
        loadOfferReports={actions.loadOfferReports}
        onChangeShowReportsModal={onChangeShowReportsModal}
        onChangeShowFoldersModal={onChangeShowFoldersModal}
        onChangeOpenDuplicateConfirmBool={onChangeOpenDuplicateConfirmBool}
        openOfferActions={openOfferActions}
        handleOpenOfferActions={handleOpenOfferActions}
        handleCloseOfferActions={handleCloseOfferActions}
        isFacilityFrame={isFacilityFrame}
        isContractModification={isContractModification}
        isFinancingOffer={isFinancingOffer}
      />
    );
  } else {
    headerPortalContent = (
      <Typography variant='subtitle1' fontWeight='bold' whiteSpace='nowrap'>
        {setTitle()}
      </Typography>
    );
  }

  useTitle(setTitle());

  return (
    <>
      <DocumentSubscriptionArea />
      <RightsContext.Provider value={rights}>
        <Grid container spacing={2} px={2}>
          <Grid item xs={12}>
            <OfferStepper
              steps={steps}
              processing={offerHeader.processing}
              onStepClick={onStepClick}
              activeStepId={activeStep}
              isImage={rights.isImage}
            />
          </Grid>
          {warnings && warnings.length > 0 && (
            <Grid item xs={12}>
              <Warnings
                warnings={warnings}
                warningsActiveTab={warningsActiveTab}
                setWarningsActiveTab={setWarningsActiveTab}
                handleDismissDocumentWorkflowError={handleDismissDocumentWorkflowError}
              ></Warnings>
            </Grid>
          )}
          <Grid item xs={12}>
            <Suspense fallback={<WrappedFakeText onPaper />}>
              <SwitcherComponent
                listContainer={offerAssetListContainer}
                createContainer={createContainer}
                container={offerContainer}
                offerFinancialContainer={offerFinancialContainer}
                offerClientContainer={offerClientContainer}
                insuranceDocumentDetailsContainer={insuranceDocumentDetailsContainer}
                onChangeActiveStep={onChangeActiveStep}
                history={history}
                documentId={offer.documentId}
                siteId={offer.siteId}
                steps={steps}
              />
            </Suspense>
          </Grid>
        </Grid>
        <Portal container={getElemByAreaKey(areas.header)}>{headerPortalContent}</Portal>
      </RightsContext.Provider>

      {offerHeader.documentId && openDuplicateConfirm && (
        <CloneOfferComponent
          offerHeader={offerHeader}
          cloneOffer={actions.cloneOffer}
          copyOffer={actions.copyOffer}
          showInfo={actions.showInfo}
          history={history}
          documentId={offer.documentId}
          siteId={offer.siteId}
          openDuplicateConfirm={openDuplicateConfirm}
          onChangeOpenDuplicateConfirmBool={onChangeOpenDuplicateConfirmBool}
          isFacilityFrame={isFacilityFrame}
        />
      )}

      {offerHeader.documentId && openReinitializationForm && (
        <OfferReinitializationComponent
          offerHeader={offerHeader}
          history={history}
          documentId={offer.documentId}
          siteId={offer.siteId}
          openReinitializationForm={openReinitializationForm}
          onChangeOpenReinitializationConfirmBool={onChangeOpenReinitializationConfirmBool}
          setReinitializationProcessing={setReinitializationProcessing}
        />
      )}

      {showReportsModal && (
        <OfferReportsContainer documentId={offer.documentId} siteId={offer.siteId} onHandleShowReportsModal={onChangeShowReportsModal} />
      )}

      {showFoldersModal && (
        <OfferFoldersContainer
          selectFolder={actions.selectFolder}
          offerHeader={offerHeader}
          onHandleShowFoldersModal={onChangeShowFoldersModal}
        />
      )}

      {showMessageNavigationStepModal && (
        <NavigationStepValidationComponent
          message={navigationStepValidationMessage}
          showModal={showMessageNavigationStepModal}
          onHandleShowModal={onChangeShowValidationMessageNavigationStep}
        />
      )}
    </>
  );
};

OfferWizard.propTypes = {
  dispatch: PropTypes.func.isRequired,
  offerHeader: PropTypes.object,
  actions: PropTypes.object.isRequired,
  config: PropTypes.object.isRequired,
  offerRights: PropTypes.object,
  warnings: PropTypes.array,
  locationPathname: PropTypes.string,
  history: PropTypes.object.isRequired,
  documentId: PropTypes.string,
  siteId: PropTypes.string,
  newOffer: PropTypes.string,
  getElemByAreaKey: PropTypes.func.isRequired,
  warningsIsValid: PropTypes.bool.isRequired,
  warningsIsLoaded: PropTypes.bool,
  rightSidebarDrawer: PropTypes.string,
  steps: PropTypes.array,
  activeStep: PropTypes.number,
  onChangeActiveStep: PropTypes.func
};

function mapStateToProps(state, props) {
  const { offerHeader } = props;

  const rightsStateKey = getDocumentRightsByStateKey(offerHeader.stateId, offerHeader.documentTypeId);
  const rights = state.documentRights[rightsStateKey]?.isLoaded ? state.documentRights[rightsStateKey] : defaultRights;

  const { isValid, loaded } = state.currentOffer.warnings;
  const { sidebarDrawer } = state.currentOffer.offerSidebarData;

  return {
    offerRights: rights,
    config: state.offerConfig,
    warnings: state.currentOffer.warnings,
    warningsIsValid: isValid,
    warningsIsLoaded: loaded,
    rightSidebarDrawer: sidebarDrawer
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadOfferConfiguration,
        resetCurrentOffer,
        submitPreApproval,
        cloneOffer,
        copyOffer,
        loadOfferWarnings,
        loadNavigationStepWarnings,
        showInfo,
        loadOfferReports,
        selectFolder,
        dismissDocumentWorkflowError,
        loadDocumentRights
      },
      dispatch
    ),
    dispatch
  };
}

export default compose(connect(mapStateToProps, mapDispatchToProps), withAreaGetter)(OfferWizard);
